import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Bell, Loader2, Send, Users } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { useClients } from "@/hooks/use-clients";
import {
  useNotificationHistory,
  useSellerUsers,
  useSendNotification,
} from "@/hooks/use-notifications";
import { usePagination } from "@/hooks/use-pagination";
import { describeCallableError } from "@/lib/callables";
import { formatDateTime, formatNumber, matches } from "@/lib/format";
import type { NotificationAudience } from "@/types";

const MAX_TITLE = 65;
const MAX_BODY = 240;
/** O mesmo limite que a callable aplica. */
const MAX_CUSTOM = 500;

const AUDIENCE_LABEL: Record<NotificationAudience, string> = {
  clients: "Todos os clientes",
  sellers: "Todos os vendedores",
  custom: "Escolher destinatários",
};

export default function Notifications() {
  const [audience, setAudience] = useState<NotificationAudience>("clients");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<Record<string, "users" | "sellerUsers">>({});
  const [confirming, setConfirming] = useState(false);

  const clients = useClients();
  const sellerUsers = useSellerUsers();
  const history = useNotificationHistory();
  const send = useSendNotification();

  const isCustom = audience === "custom";
  const chosenIds = useMemo(() => Object.keys(chosen), [chosen]);

  const candidates = useMemo(() => {
    if (!isCustom) return [];
    const rows: { id: string; name: string; detail: string; kind: "users" | "sellerUsers" }[] = [];
    for (const c of clients.data ?? []) {
      rows.push({
        id: c.id,
        name: c.name || "Sem nome",
        detail: c.email || "Cliente",
        kind: "users",
      });
    }
    for (const s of sellerUsers.data ?? []) {
      rows.push({
        id: s.id,
        name: s.name || "Sem nome",
        detail: s.email || "Vendedor",
        kind: "sellerUsers",
      });
    }
    if (!search) return rows;
    return rows.filter((r) => matches(r.name, search) || matches(r.detail, search));
  }, [isCustom, clients.data, sellerUsers.data, search]);
  const candidatesPage = usePagination(candidates, search, 20);

  const reachEstimate = useMemo(() => {
    if (audience === "clients") return clients.data?.length ?? 0;
    if (audience === "sellers") return sellerUsers.data?.length ?? 0;
    return chosenIds.length;
  }, [audience, clients.data, sellerUsers.data, chosenIds]);

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (!isCustom || chosenIds.length > 0) &&
    !send.isPending;

  function toggle(id: string, kind: "users" | "sellerUsers") {
    setChosen((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else if (Object.keys(next).length < MAX_CUSTOM) next[id] = kind;
      return next;
    });
  }

  async function confirmSend() {
    try {
      const result = await send.mutateAsync({
        audience,
        title: title.trim(),
        body: body.trim(),
        userIds: isCustom
          ? chosenIds.filter((id) => chosen[id] === "users")
          : undefined,
        sellerUserIds: isCustom
          ? chosenIds.filter((id) => chosen[id] === "sellerUsers")
          : undefined,
      });
      toast.success(
        `Enviada para ${formatNumber(result.recipients)} destinatários ` +
          `(${formatNumber(result.devices)} dispositivos).`,
      );
      setTitle("");
      setBody("");
      setChosen({});
      setConfirming(false);
    } catch (error) {
      // O histórico registra o disparo mesmo assim, e é onde a falha aparece.
      toast.error(describeCallableError(error));
      setConfirming(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Notificações"
        description="Envio dirigido de aviso por push, para clientes, vendedores ou uma lista escolhida."
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <Card className="xl:col-span-3">
          <CardHeader>
            <CardTitle>Nova notificação</CardTitle>
            <CardDescription>
              Chega como push no celular. Não há como apagar depois de enviada.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="audience">Quem recebe</Label>
              <Select
                value={audience}
                onValueChange={(v) => setAudience(v as NotificationAudience)}
              >
                <SelectTrigger id="audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clients">{AUDIENCE_LABEL.clients}</SelectItem>
                  <SelectItem value="sellers">{AUDIENCE_LABEL.sellers}</SelectItem>
                  <SelectItem value="custom">{AUDIENCE_LABEL.custom}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-caption text-muted-foreground">
                {isCustom
                  ? `${formatNumber(chosenIds.length)} escolhidos, de no máximo ${MAX_CUSTOM}.`
                  : `Alcança cerca de ${formatNumber(reachEstimate)} contas com aplicativo instalado.`}
              </p>
            </div>

            {isCustom ? (
              <div className="space-y-2">
                <Label htmlFor="destinatarios">Destinatários</Label>
                <Input
                  id="destinatarios"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome ou e-mail"
                />
                <div className="max-h-64 overflow-y-auto rounded-md border border-border">
                  {clients.isLoading || sellerUsers.isLoading ? (
                    <div className="space-y-2 p-3">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : candidates.length === 0 ? (
                    <p className="p-4 text-small text-muted-foreground">
                      Ninguém encontrado com esse termo.
                    </p>
                  ) : (
                    <ul>
                      {candidatesPage.pageItems.map((row) => (
                        <li key={`${row.kind}-${row.id}`}>
                          <button
                            type="button"
                            onClick={() => toggle(row.id, row.kind)}
                            className="flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <span
                              aria-hidden
                              className={`size-4 shrink-0 rounded border ${
                                chosen[row.id]
                                  ? "border-primary bg-primary"
                                  : "border-muted-foreground/40"
                              }`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-small font-semibold">
                                {row.name}
                              </span>
                              <span className="block truncate text-caption text-muted-foreground">
                                {row.detail}
                              </span>
                            </span>
                            <Badge variant={row.kind === "users" ? "muted" : "outline"}>
                              {row.kind === "users" ? "Cliente" : "Vendedor"}
                            </Badge>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Pagination
                  {...candidatesPage.paginationProps}
                  itemLabel="contas"
                  className="rounded-md border px-2"
                />
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={title}
                maxLength={MAX_TITLE}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Manutenção programada"
              />
              <p className="text-caption text-muted-foreground">
                {title.length} de {MAX_TITLE}. O celular corta o que passa disso.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="body">Mensagem</Label>
              <Textarea
                id="body"
                value={body}
                maxLength={MAX_BODY}
                rows={4}
                onChange={(e) => setBody(e.target.value)}
                placeholder="O aplicativo fica indisponível neste domingo, das 2h às 4h."
              />
              <p className="text-caption text-muted-foreground">
                {body.length} de {MAX_BODY}.
              </p>
            </div>

            <div className="rounded-md border border-state-warning/40 bg-state-warning/10 p-3">
              <p className="flex items-start gap-2 text-caption">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-state-warning" />
                <span>
                  Notificação enviada não volta atrás, e chega no celular de quem estiver
                  com o aplicativo instalado. Confira o texto antes.
                </span>
              </p>
            </div>

            <Button onClick={() => setConfirming(true)} disabled={!canSend}>
              {send.isPending ? <Loader2 className="animate-spin" /> : <Send />}
              Enviar notificação
            </Button>
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Histórico</CardTitle>
            <CardDescription>Todos os envios, do mais recente para o mais antigo, com o resultado de cada um.</CardDescription>
          </CardHeader>
          <CardContent>
            {history.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : history.rows.length === 0 ? (
              <EmptyState
                icon={Bell}
                title="Nenhum envio ainda"
                description="O que for enviado daqui aparece nesta lista, inclusive o que falhar."
              />
            ) : (
              <ul className="space-y-3">
                {history.rows.map((item) => (
                  <li key={item.id} className="rounded-md border border-border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="min-w-0 flex-1 font-semibold">{item.title}</p>
                      {item.error ? (
                        <Badge variant="error">Falhou</Badge>
                      ) : (
                        <Badge variant="ok">Enviada</Badge>
                      )}
                    </div>
                    <p className="mt-1 text-small text-muted-foreground">{item.body}</p>
                    <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Users className="size-3.5" />
                        {item.audience ? AUDIENCE_LABEL[item.audience] : "Envio"}
                      </span>
                      <span>
                        {formatNumber(item.successCount ?? 0)} de{" "}
                        {formatNumber(item.devices ?? 0)} dispositivos
                      </span>
                      <span>{formatDateTime(item.sentAt)}</span>
                    </p>
                    {item.withoutToken ? (
                      <p className="mt-1 text-caption text-muted-foreground">
                        {formatNumber(item.withoutToken)} sem dispositivo registrado.
                      </p>
                    ) : null}
                    {item.error ? (
                      <p className="mt-1 text-caption text-state-error">{item.error}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            <Pagination
              {...history.paginationProps}
              itemLabel="envios"
              className="-mx-4 mt-3 sm:-mx-5"
            />
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogTitle>Enviar para {AUDIENCE_LABEL[audience].toLowerCase()}?</AlertDialogTitle>
          <AlertDialogDescription>
            {isCustom
              ? `${formatNumber(chosenIds.length)} destinatários escolhidos.`
              : `Cerca de ${formatNumber(reachEstimate)} contas.`}{" "}
            A notificação chega no celular e não pode ser cancelada depois de enviada.
          </AlertDialogDescription>
          <div className="rounded-md border border-border p-3">
            <p className="font-semibold">{title}</p>
            <p className="mt-1 text-small text-muted-foreground">{body}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmSend()} disabled={send.isPending}>
              {send.isPending ? <Loader2 className="animate-spin" /> : null}
              Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
